import { Currencies, DaysToSubtractFromDeliveryDate } from '@/enums/general'
import { BoysPrices, MenPrices, SocksDiscountPrices, SocksPrices, UndershirtsDiscountPrices, UndershirtsPrices, WomenPrices } from '@/enums/products'
import { addLocalCurrency, convertSrc } from '@/utils/general'
import { GET_COUNTRY_DATA, AUTH_USER } from '@/utils/store/getterTypes'
import { mapState, mapGetters } from 'vuex'

export default {
  computed: {
    textVariables () {
      return {
        ...this.countryVariables,
        ...this.userVariables
      }
    },
    userVariables () {
      if (!this.authUser) {
        return {}
      }

      const nextPaymentDate = this.authUser.user?.nextPaymentDate ? this.$moment.utc(this.authUser.user.nextPaymentDate) : null

      return {
        ...(nextPaymentDate ? {
          nextPaymentDate: nextPaymentDate.date(),
          formattedNextPaymentDate: `${nextPaymentDate.date()} ${this.$t(`Months[${nextPaymentDate.month()}]`)} ${nextPaymentDate.format('YYYY')}`
        } : {})
      }
    },
    countryVariables () {
      if (!this.countryData) {
        return {}
      }

      const date = this.$moment.utc(this.countryData.dailyOrderDeliveryDate)
      const latest = date.diff(this.$moment.utc().startOf('day'), 'days')
      const earliest = latest - DaysToSubtractFromDeliveryDate
      const nextPaymentDate = this.$moment.utc(this.countryData.nextPaymentDates[0])

      const nextOrderCreationDate = this.$moment.utc(this.countryData.nextOrderDates[0])

      return {
        // Country specific variables
        trialDuration: this.countryData.trialDuration,
        trialDays: this.countryData.trialDuration,
        country: `${this.$t(`Countries.${this.countryData.code.toLowerCase()}`)}`,
        waitingDays: this.countryData.waitingDays,
        waitDays: this.countryData.waitingDays,
        earliest,
        deliveryEarliest: earliest,
        latest,
        deliveryLatest: latest,
        deliveryDays: latest,
        monthlyEarliest: 17,
        monthlyLatest: 23,
        undershirtsPrice: addLocalCurrency(UndershirtsPrices[this.country], this.mixinCountryCode, this.mixinCurrency),
        undershirtsDiscountPrice: addLocalCurrency(UndershirtsDiscountPrices[this.country], this.mixinCountryCode, this.mixinCurrency),
        socksPrice: addLocalCurrency(SocksPrices[this.country], this.mixinCountryCode, this.mixinCurrency),
        sockPriceWithoutDiscount: addLocalCurrency(SocksPrices[this.country], this.mixinCountryCode, this.mixinCurrency),
        socksDiscountPrice: addLocalCurrency(SocksDiscountPrices[this.country], this.mixinCountryCode, this.mixinCurrency),
        sockPriceWithDiscount: addLocalCurrency(SocksDiscountPrices[this.country], this.mixinCountryCode, this.mixinCurrency),
        menPrice: addLocalCurrency(MenPrices[this.country], this.mixinCountryCode, this.mixinCurrency),
        boysPrice: addLocalCurrency(BoysPrices[this.country], this.mixinCountryCode, this.mixinCurrency),
        womenPrice: addLocalCurrency(WomenPrices[this.country], this.mixinCountryCode, this.mixinCurrency),
        freePrice: addLocalCurrency(0, this.mixinCountryCode, this.mixinCurrency),
        email: this.$t('Email'),

        // Dates
        nextPaymentDate: nextPaymentDate.date(),
        formattedNextPaymentDate: `${nextPaymentDate.date()} ${this.$t(`Months[${nextPaymentDate.month()}]`)} ${nextPaymentDate.format('YYYY')}`,
        nextOrderCreationDate: nextOrderCreationDate.date(),
        formattedNextOrderCreationDate: `${nextOrderCreationDate.date()} ${this.$t(`Months[${nextOrderCreationDate.month()}]`)} ${nextOrderCreationDate.format('YYYY')}`,

        // Loyalty specific variables
        loyaltyAmount: addLocalCurrency(10, this.mixinCountryCode, Currencies.nl),
        twentyAmount: addLocalCurrency(20, this.mixinCountryCode, Currencies.nl),
        maxLoyaltyAmount: addLocalCurrency(20, this.mixinCountryCode, Currencies.nl),

        // Links
        membershipsLink: `<a href="/account/memberships">${this.$t('FAQ.LinkWords.Memberships')}</a>`,
        signup: `<a href="/signup">${this.$t('FAQ.Variables.WhenYouSignUp')}</a>`,
        womenSignup: `<a href="/women/signup">${this.$t('FAQ.Variables.WhenYouSignUp')}</a>`,
        login: `<a href="/login">${this.$t('FAQ.Variables.Login')}</a>`,
        address: `<a href="/account/memberships/overview/address">${this.$t('FAQ.Variables.Login')}</a>`,
        memberships: `<a href="/account/memberships">${this.$t('FAQ.Variables.Login')}</a>`,
        orders: `<a href="/account/orders">${this.$t('FAQ.Variables.Login')}</a>`,
        rightOfWithdrawal: `<a href="/right-of-withdrawal">${this.$t('FAQ.LinkWords.RightOfWithdrawal')}</a>`,
        withdrawalDownload: `<a href="${convertSrc(`/images/frontend/legal/ota-withdrawal-form-${this.locale}.pdf`, true)}" target="_blank">${this.$t('RightOfWithdrawal.General.LinkWord')}</a>`,

        freesocks: `<a href="/login">${this.$t('FAQ.Variables.FreeSocks')}</a>`, // TODO::

        contact: `<a href="https://customerservice.onthatass.com/${this.locale}">${this.$t('FAQ.Variables.Contact')}</a>`,

        privacystatement: `<a href="/privacy-statement">${this.$t('FAQ.Variables.Privacystatement')}</a>`,
        termsofuse: `<a href="/terms-of-use">${this.$t('PrivacyStatement.General.Here')}</a>`,

        freeboxershort: `<a href="/login">${this.$t('FAQ.Variables.FreeBoxershort')}</a>`, // TODO::
        womenSize: `<a href="/women/bra-size">${this.$t('PrivacyStatement.General.Here')}</a>`, // TODO::

        overviewBoxershorts: `<a href="/account/memberships/overview/boxershorts">${this.$t('FAQ.LinkWords.OverviewBoxershorts')}</a>`,
        overviewAddress: `<a href="/account/memberships/overview/address">${this.$t('FAQ.LinkWords.OverviewAddress')}</a>`,
        overviewStatus: `<a href="/account/memberships/overview/status">${this.$t('FAQ.LinkWords.OverviewStatus')}</a>`,
        overviewMember: `<a href="/account/memberships/overview/member">${this.$t('FAQ.LinkWords.OverviewMember')}</a>`,

        loyaltyProgram: `<a href="/account/loyalty-program">${this.$t('FAQ.LinkWords.LoyaltyProgram')}</a>`,
        webshop: `<a href="/webshop">${this.$t('FAQ.LinkWords.Webshop')}</a>`,
        security: `<a href="/account/personal-details/security">${this.$t('FAQ.LinkWords.Security')}</a>`,
        settings: `<a href="/account/personal-details">${this.$t('FAQ.LinkWords.Settings')}</a>`,

        sizechart: `<a href="/men/signup/sizechart">${this.$t('FAQ.Variables.SizeChart')}</a>`,
        sustainability: `<a href="/sustainability">${this.$t('FAQ.Variables.Sustainability')}</a>`,
        terms: `<a href="/terms-and-conditions">${this.$t('FAQ.Variables.Terms')}</a>`,
        lookbook: `<a href="/lookbook/men-originals">${this.$t('FAQ.Variables.Lookbook')}</a>`,
        womenSizeChartButton: `<button id="women-size-chart-btn">${this.$t('PrivacyStatement.General.Here')}</button>`,

        // Formatting
        breakLine: '<br>',
        br: '<br />'
      }
    },
    mixinCountryCode () {
      return `${this.locale}-${this.country}`
    },
    mixinCurrency () {
      if (!this.countryData || !this.countryData.currency) {
        return this.mixinCurrency
      }

      return this.countryData.currency
    },
    ...mapState('app', {
      locale: state => state.locale,
      country: state => state.country
    }),
    ...mapGetters('app', {
      countryData: GET_COUNTRY_DATA
    }),
    ...mapGetters('auth', {
      authUser: AUTH_USER
    })
  }
}
